/**
 * KAN-752: предупреждение при уходе со страницы с несохранёнными правками.
 *
 * Приложение работает на <BrowserRouter> (не data router), поэтому useBlocker
 * из react-router недоступен. Вместо него:
 * - beforeunload: закрытие вкладки, перезагрузка, переход на другой сайт;
 * - перехват кликов по внутренним ссылкам (меню, хлебные крошки, "Назад"):
 *   window.confirm, при отказе переход отменяется.
 * Кнопки браузера "назад/вперёд" внутри приложения этим не ловятся.
 */
import { useEffect } from 'react';

export const UNSAVED_CHANGES_CONFIRM =
  'Есть несохранённые изменения. Уйти со страницы и потерять их?';

function isPlainLeftClick(event: MouseEvent): boolean {
  return (
    event.button === 0 &&
    !event.defaultPrevented &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

/** Ссылка уводит с текущей страницы внутри приложения (не новая вкладка, не тот же адрес). */
function leavingLink(target: EventTarget | null): HTMLAnchorElement | null {
  const anchor = target instanceof Element ? target.closest('a[href]') : null;
  if (!(anchor instanceof HTMLAnchorElement)) return null;
  if (anchor.target && anchor.target !== '_self') return null;
  if (anchor.hasAttribute('download')) return null;
  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) return null;
  const same =
    url.pathname === window.location.pathname && url.search === window.location.search;
  return same ? null : anchor;
}

export function useUnsavedChangesGuard(
  when: boolean,
  message: string = UNSAVED_CHANGES_CONFIRM
): void {
  useEffect(() => {
    if (!when) return undefined;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Старые браузеры показывают диалог только при заданном returnValue.
      event.returnValue = message;
      return message;
    };

    const onClick = (event: MouseEvent) => {
      if (!isPlainLeftClick(event) || !leavingLink(event.target)) return;
      if (window.confirm(message)) return;
      event.preventDefault();
      event.stopPropagation();
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    // capture: раньше обработчика <Link>, который сам делает navigate()
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [when, message]);
}
